/**
 * Intake Target Selector - Hybrid dropdown + card UI for selecting intake type
 *
 * DRR-220: Expanded intake targets with categories and PII vault indicators.
 *
 * Design:
 * - Searchable dropdown for power users (15+ targets)
 * - Category cards for discoverability
 * - Recently used quick-access
 * - PII vault badge for sensitive document types
 *
 * FERP Principles:
 * - Dumb Frontend: All targets defined by backend enum
 * - Mantine v8.x only
 * - Domain-agnostic labels (Subject not Resident)
 */

import { useState, useMemo, useCallback, useEffect } from 'react';
import {
  Stack,
  Text,
  SimpleGrid,
  Paper,
  UnstyledButton,
  Group,
  ThemeIcon,
  Badge,
  Combobox,
  TextInput,
  useCombobox,
  ScrollArea,
  Divider,
  Collapse,
  Box,
  Tooltip,
} from '@mantine/core';
import {
  IconSearch,
  IconFileAnalytics,
  IconCertificate,
  IconListCheck,
  IconForms,
  IconTable,
  IconBook,
  IconReceipt,
  IconUser,
  IconId,
  IconSchool,
  IconShieldCheck,
  IconUserPlus,
  IconFileInvoice,
  IconSignature,
  IconBuildingBank,
  IconChevronDown,
  IconChevronUp,
  IconLock,
  IconHistory,
  type Icon,
} from '@tabler/icons-react';

// ============================================================
// Types
// ============================================================

export interface IntakeTarget {
  value: string;
  label: string;
  description: string;
  icon: Icon;
  color: string;
  category: IntakeCategory;
  requiresPiiVault: boolean;
  /** System-only targets hidden from normal UI */
  systemOnly?: boolean;
}

export type IntakeCategory =
  | 'documents'
  | 'employee'
  | 'subject'
  | 'financial'
  | 'configuration';

interface CategoryMeta {
  label: string;
  description: string;
  icon: Icon;
  color: string;
}

// ============================================================
// Constants
// ============================================================

const CATEGORY_META: Record<IntakeCategory, CategoryMeta> = {
  documents: {
    label: 'Documents',
    description: 'General documents, regulatory files, evidence',
    icon: IconFileAnalytics,
    color: 'violet',
  },
  employee: {
    label: 'Employee / Workforce',
    description: 'HR documents, credentials, training records',
    icon: IconUser,
    color: 'blue',
  },
  subject: {
    label: 'Subject / Client',
    description: 'Intake forms, insurance, consents',
    icon: IconUserPlus,
    color: 'teal',
  },
  financial: {
    label: 'Financial',
    description: 'Invoices, receipts, vendor contracts',
    icon: IconReceipt,
    color: 'cyan',
  },
  configuration: {
    label: 'Configuration',
    description: 'Task groups, form templates, imports',
    icon: IconTable,
    color: 'grape',
  },
};

/**
 * All intake targets matching the backend GqlIntakeTarget enum (DRR-220).
 * Ordered by category for grouped display.
 */
export const INTAKE_TARGETS: IntakeTarget[] = [
  // ── Documents ────────────────────────────────────────────────────────────
  {
    value: 'DOCUMENT',
    label: 'General Document',
    description: 'Convert to workflow template via AI pipeline',
    icon: IconFileAnalytics,
    color: 'violet',
    category: 'documents',
    requiresPiiVault: false,
  },
  {
    value: 'REGULATORY_DOCUMENT',
    label: 'Regulatory Document',
    description: 'Extract rules to RuleSet config (rs-*)',
    icon: IconCertificate,
    color: 'red',
    category: 'documents',
    requiresPiiVault: false,
  },
  {
    value: 'EVIDENCE_PACKAGE',
    label: 'Evidence Package',
    description: 'Attach evidence to existing Issues or Tasks',
    icon: IconCertificate,
    color: 'orange',
    category: 'documents',
    requiresPiiVault: false,
  },

  // ── Employee / Workforce ─────────────────────────────────────────────────
  {
    value: 'EMPLOYEE_DOCUMENT',
    label: 'Employee Document',
    description: 'I-9, W-4, direct deposit, signed handbook',
    icon: IconId,
    color: 'blue',
    category: 'employee',
    requiresPiiVault: true,
  },
  {
    value: 'LICENSE_CREDENTIAL',
    label: 'License / Credential',
    description: 'Professional license with expiry tracking',
    icon: IconCertificate,
    color: 'indigo',
    category: 'employee',
    requiresPiiVault: true,
  },
  {
    value: 'TRAINING_CERTIFICATE',
    label: 'Training Certificate',
    description: 'CE record linked to competency groups',
    icon: IconSchool,
    color: 'cyan',
    category: 'employee',
    requiresPiiVault: false,
  },
  {
    value: 'BACKGROUND_CHECK',
    label: 'Background Check',
    description: '245C compliance, BCH authorization/results',
    icon: IconShieldCheck,
    color: 'red',
    category: 'employee',
    requiresPiiVault: true,
  },

  // ── Subject / Client / Patient ───────────────────────────────────────────
  {
    value: 'SUBJECT_INTAKE',
    label: 'Subject Intake',
    description: 'New client/patient/resident intake packet',
    icon: IconUserPlus,
    color: 'teal',
    category: 'subject',
    requiresPiiVault: true,
  },
  {
    value: 'INSURANCE_DOCUMENT',
    label: 'Insurance Document',
    description: 'Insurance card, policy, benefits enrollment',
    icon: IconBuildingBank,
    color: 'green',
    category: 'subject',
    requiresPiiVault: true,
  },
  {
    value: 'CONSENT_FORM',
    label: 'Consent Form',
    description: 'HIPAA authorization, photo release, DNR/POLST',
    icon: IconSignature,
    color: 'lime',
    category: 'subject',
    requiresPiiVault: false,
  },

  // ── Financial ────────────────────────────────────────────────────────────
  {
    value: 'FINANCIAL_DOCUMENT',
    label: 'Financial Document',
    description: 'Receipt, invoice, bill for ERP posting',
    icon: IconReceipt,
    color: 'cyan',
    category: 'financial',
    requiresPiiVault: false,
  },
  {
    value: 'VENDOR_CONTRACT',
    label: 'Vendor Contract',
    description: 'Supplier agreement with approval workflow',
    icon: IconFileInvoice,
    color: 'pink',
    category: 'financial',
    requiresPiiVault: false,
  },

  // ── Configuration ────────────────────────────────────────────────────────
  {
    value: 'TASK_GROUP_DEFINITION',
    label: 'Task Group',
    description: 'Import as task group with care-graph edges',
    icon: IconListCheck,
    color: 'teal',
    category: 'configuration',
    requiresPiiVault: false,
  },
  {
    value: 'FORM_TEMPLATE',
    label: 'Form Template',
    description: 'Build FormSchema backend definition (ft-*)',
    icon: IconForms,
    color: 'blue',
    category: 'configuration',
    requiresPiiVault: false,
  },
  {
    value: 'STRUCTURED_IMPORT',
    label: 'Structured Import',
    description: 'CSV/JSON batch primitive creation',
    icon: IconTable,
    color: 'green',
    category: 'configuration',
    requiresPiiVault: false,
  },
  {
    value: 'CARE_VOCABULARY',
    label: 'Care Vocabulary',
    description: 'Extend care vocabulary tag namespace',
    icon: IconBook,
    color: 'grape',
    category: 'configuration',
    requiresPiiVault: false,
  },

  // ── System-only (hidden from normal UI) ──────────────────────────────────
  {
    value: 'LEGISLATION_FEEDBACK',
    label: 'Legislation Feedback',
    description: 'Tenant feedback on pending legislation',
    icon: IconFileAnalytics,
    color: 'gray',
    category: 'configuration',
    requiresPiiVault: false,
    systemOnly: true,
  },
  {
    value: 'ADVOCACY_AGGREGATE',
    label: 'Advocacy Aggregate',
    description: 'System-generated advocacy position',
    icon: IconFileAnalytics,
    color: 'gray',
    category: 'configuration',
    requiresPiiVault: false,
    systemOnly: true,
  },
];

/** User-visible targets (excludes system-only) */
const VISIBLE_TARGETS = INTAKE_TARGETS.filter((t) => !t.systemOnly);

/** Get target by value */
export function getIntakeTarget(value: string): IntakeTarget | undefined {
  return INTAKE_TARGETS.find((t) => t.value === value);
}

// ============================================================
// Local Storage for Recently Used
// ============================================================

const RECENT_STORAGE_KEY = 'ferp:intake:recent';
const MAX_RECENT = 4;

function getRecentTargets(): string[] {
  try {
    const stored = localStorage.getItem(RECENT_STORAGE_KEY);
    return stored ? JSON.parse(stored) : [];
  } catch {
    return [];
  }
}

function addRecentTarget(value: string): void {
  try {
    const recent = getRecentTargets().filter((v) => v !== value);
    recent.unshift(value);
    localStorage.setItem(
      RECENT_STORAGE_KEY,
      JSON.stringify(recent.slice(0, MAX_RECENT))
    );
  } catch {
    // Ignore storage errors
  }
}

// ============================================================
// Component
// ============================================================

export interface IntakeTargetSelectorProps {
  value: string;
  onChange: (value: string) => void;
  /** Show expanded category cards (default: true) */
  showCategories?: boolean;
  /** Disable selection */
  disabled?: boolean;
}

export function IntakeTargetSelector({
  value,
  onChange,
  showCategories = true,
  disabled = false,
}: IntakeTargetSelectorProps) {
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedCategories, setExpandedCategories] = useState<Set<IntakeCategory>>(
    new Set(['documents', 'employee', 'subject'])
  );
  const [recentValues, setRecentValues] = useState<string[]>([]);
  const combobox = useCombobox();

  // Load recent targets on mount
  useEffect(() => {
    setRecentValues(getRecentTargets());
  }, []);

  const selectedTarget = useMemo(
    () => INTAKE_TARGETS.find((t) => t.value === value),
    [value]
  );

  // Filter targets by search query
  const filteredTargets = useMemo(() => {
    if (!searchQuery.trim()) return VISIBLE_TARGETS;
    const query = searchQuery.toLowerCase();
    return VISIBLE_TARGETS.filter(
      (t) =>
        t.label.toLowerCase().includes(query) ||
        t.description.toLowerCase().includes(query) ||
        t.category.toLowerCase().includes(query)
    );
  }, [searchQuery]);

  // Group targets by category
  const targetsByCategory = useMemo(() => {
    const grouped = new Map<IntakeCategory, IntakeTarget[]>();
    for (const target of filteredTargets) {
      const list = grouped.get(target.category) || [];
      list.push(target);
      grouped.set(target.category, list);
    }
    return grouped;
  }, [filteredTargets]);

  // Recent targets (resolved to full objects)
  const recentTargets = useMemo(() => {
    return recentValues
      .map((v) => VISIBLE_TARGETS.find((t) => t.value === v))
      .filter((t): t is IntakeTarget => t !== undefined)
      .slice(0, MAX_RECENT);
  }, [recentValues]);

  const handleSelect = useCallback(
    (targetValue: string) => {
      onChange(targetValue);
      addRecentTarget(targetValue);
      setRecentValues(getRecentTargets());
      combobox.closeDropdown();
    },
    [onChange, combobox]
  );

  const toggleCategory = useCallback((category: IntakeCategory) => {
    setExpandedCategories((prev) => {
      const next = new Set(prev);
      if (next.has(category)) {
        next.delete(category);
      } else {
        next.add(category);
      }
      return next;
    });
  }, []);

  // Render a single target option (used in dropdown and cards)
  const renderTargetOption = useCallback(
    (target: IntakeTarget, compact = false) => {
      const Icon = target.icon;
      const isSelected = target.value === value;

      return (
        <UnstyledButton
          key={target.value}
          onClick={() => handleSelect(target.value)}
          disabled={disabled}
          style={{ width: '100%' }}
        >
          <Paper
            withBorder
            p={compact ? 'xs' : 'sm'}
            radius="md"
            style={{
              borderColor: isSelected
                ? `var(--mantine-color-${target.color}-6)`
                : undefined,
              backgroundColor: isSelected
                ? `var(--mantine-color-${target.color}-0)`
                : undefined,
              opacity: disabled ? 0.6 : 1,
              cursor: disabled ? 'not-allowed' : 'pointer',
            }}
          >
            <Group gap="xs" wrap="nowrap">
              <ThemeIcon
                size={compact ? 'sm' : 'md'}
                variant={isSelected ? 'filled' : 'light'}
                color={target.color}
              >
                <Icon size={compact ? 14 : 16} />
              </ThemeIcon>
              <div style={{ flex: 1, minWidth: 0 }}>
                <Group gap={4} wrap="nowrap">
                  <Text size={compact ? 'xs' : 'sm'} fw={600} truncate>
                    {target.label}
                  </Text>
                  {target.requiresPiiVault && (
                    <Tooltip label="Requires PII Vault (sensitive data)">
                      <Badge
                        size="xs"
                        variant="light"
                        color="red"
                        leftSection={<IconLock size={10} />}
                      >
                        PII
                      </Badge>
                    </Tooltip>
                  )}
                </Group>
                {!compact && (
                  <Text size="xs" c="dimmed" lineClamp={1}>
                    {target.description}
                  </Text>
                )}
              </div>
            </Group>
          </Paper>
        </UnstyledButton>
      );
    },
    [value, disabled, handleSelect]
  );

  return (
    <Stack gap="sm">
      {/* Searchable Dropdown */}
      <Combobox
        store={combobox}
        onOptionSubmit={handleSelect}
        withinPortal={false}
      >
        <Combobox.Target>
          <TextInput
            placeholder="Search intake types..."
            leftSection={<IconSearch size={16} />}
            rightSection={
              selectedTarget && (
                <Badge size="sm" variant="light" color={selectedTarget.color}>
                  {selectedTarget.label}
                </Badge>
              )
            }
            rightSectionWidth={selectedTarget ? 140 : undefined}
            value={searchQuery}
            onChange={(e) => {
              setSearchQuery(e.currentTarget.value);
              combobox.openDropdown();
            }}
            onFocus={() => combobox.openDropdown()}
            disabled={disabled}
          />
        </Combobox.Target>

        <Combobox.Dropdown>
          <Combobox.Options>
            <ScrollArea.Autosize mah={300}>
              {filteredTargets.length === 0 ? (
                <Combobox.Empty>No matching intake types</Combobox.Empty>
              ) : (
                filteredTargets.map((target) => (
                  <Combobox.Option
                    key={target.value}
                    value={target.value}
                    active={target.value === value}
                  >
                    <Group gap="xs" wrap="nowrap">
                      <ThemeIcon size="sm" variant="light" color={target.color}>
                        <target.icon size={14} />
                      </ThemeIcon>
                      <div style={{ flex: 1 }}>
                        <Group gap={4}>
                          <Text size="sm">{target.label}</Text>
                          {target.requiresPiiVault && (
                            <Badge size="xs" variant="light" color="red">
                              PII
                            </Badge>
                          )}
                        </Group>
                        <Text size="xs" c="dimmed">
                          {target.description}
                        </Text>
                      </div>
                    </Group>
                  </Combobox.Option>
                ))
              )}
            </ScrollArea.Autosize>
          </Combobox.Options>
        </Combobox.Dropdown>
      </Combobox>

      {/* Recently Used */}
      {recentTargets.length > 0 && (
        <div>
          <Group gap={4} mb="xs">
            <IconHistory size={14} color="var(--mantine-color-dimmed)" />
            <Text size="xs" c="dimmed">
              Recently used
            </Text>
          </Group>
          <SimpleGrid cols={{ base: 2, sm: 4 }} spacing="xs">
            {recentTargets.map((target) => renderTargetOption(target, true))}
          </SimpleGrid>
        </div>
      )}

      {/* Category Cards */}
      {showCategories && (
        <Stack gap="xs">
          <Divider
            label={
              <Text size="xs" c="dimmed">
                All categories
              </Text>
            }
          />
          {(
            ['documents', 'employee', 'subject', 'financial', 'configuration'] as IntakeCategory[]
          ).map((category) => {
            const meta = CATEGORY_META[category];
            const targets = targetsByCategory.get(category) || [];
            const isExpanded = expandedCategories.has(category);
            const CategoryIcon = meta.icon;

            if (targets.length === 0) return null;

            return (
              <Paper key={category} withBorder radius="md" p="xs">
                <UnstyledButton
                  onClick={() => toggleCategory(category)}
                  style={{ width: '100%' }}
                >
                  <Group justify="space-between">
                    <Group gap="xs">
                      <ThemeIcon size="sm" variant="light" color={meta.color}>
                        <CategoryIcon size={14} />
                      </ThemeIcon>
                      <div>
                        <Text size="sm" fw={600}>
                          {meta.label}
                        </Text>
                        <Text size="xs" c="dimmed">
                          {meta.description}
                        </Text>
                      </div>
                    </Group>
                    <Box c="dimmed">
                      {isExpanded ? (
                        <IconChevronUp size={14} />
                      ) : (
                        <IconChevronDown size={14} />
                      )}
                    </Box>
                  </Group>
                </UnstyledButton>

                <Collapse in={isExpanded}>
                  <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="xs" mt="xs">
                    {targets.map((target) => renderTargetOption(target))}
                  </SimpleGrid>
                </Collapse>
              </Paper>
            );
          })}
        </Stack>
      )}
    </Stack>
  );
}

export default IntakeTargetSelector;
